import { useState, useEffect } from "react";
import Link from "next/link";

import { CATEGORIES_MAP } from "_constants/defs";
import TEXT from "localisation";

import logInternalLinkClick from "lib/ga/internalLinkClick";
import buttons from "components/_styled/buttons.module.css";

import styles from './categorise.module.css';

type props = {
  activePortfolio: {
    data: {
      description: string,
      dark: string,
      main: string
    },
    uri: string,
    name: string,
    id: string,
    propertyCategories: object,
    schemeCategories: object
  }
}

const PortfolioCategoryTabs = ({ activePortfolio }: props) => {
  const [activeTab, setActiveTab] = useState('');
  const [schemeOrder, setSchemeOrder] = useState({});
  const [propertiesOrder, setPropertiesOrder] = useState<object | null>(null);

  const { propertyCategories, schemeCategories, uri, data } = activePortfolio;
  const thisLocation = `/portfolio/${uri}`;
  const propertiesCount = Object.keys(propertyCategories).length;
  const propertiesExist = !!propertiesCount;
  const schemesCount = Object.keys(schemeCategories).length;
  const schemesExist = !!schemesCount;

  const theme = {
    dark: data.dark,
    light: data[`To Let`],
  };

  const LandSecOrdering = (categories) => {
    let newOrder = {};
    let oldOrder = categories;

    if (categories['SC']) {
      newOrder['SC'] = { ...categories['SC'] }
      newOrder['SC']['image'] = `${process.env.NEXT_PUBLIC_APP_CDN_URL}portfolios/_categories/category_landsec_SC.webp`
    }

    if (categories['LE']) {
      newOrder['LE'] = { ...categories['LE'] }
      newOrder['LE']['image'] = `${process.env.NEXT_PUBLIC_APP_CDN_URL}portfolios/_categories/category_landsec_LE.webp`
    }

    if (categories['RW']) {
      newOrder['RW'] = { ...categories['RW'] }
      newOrder['RW']['image'] = `${process.env.NEXT_PUBLIC_APP_CDN_URL}portfolios/_categories/category_landsec_RW.webp`
    }

    return { ...newOrder, ...oldOrder }
  }

  const createPropertiesTab = (() => {
    if (propertiesOrder === null) {
      if (uri === 'Landsec') {
        setPropertiesOrder(LandSecOrdering(propertyCategories))
      } else {
        setPropertiesOrder(propertyCategories)
      }
    }
    setActiveTab("properties");
  })

  useEffect(() => {
    if (!activeTab) {
      if (schemesExist) {
        if (uri === 'Landsec') {
          setSchemeOrder(LandSecOrdering(schemeCategories))
        } else {
          setSchemeOrder(schemeCategories)
        }
        setActiveTab("schemes");
      } else if (propertiesExist) {
        if (uri === 'Landsec') {
          setPropertiesOrder(LandSecOrdering(propertyCategories))
        } else {
          setPropertiesOrder(propertyCategories)
        }
        setActiveTab("properties");
      } else {
        setActiveTab("no-results");
      }
    }
  }, []);



  return (
    <div className={styles.TabsContainer}>
      <div className={styles.TabsHeaderContainer}>
        <div className={styles.TabsHeader}>
          <ul className={styles.Tabs}>
            {schemesExist && (
              <li className={styles.Tab}>
                <button className={styles.TabLink}
                  style={{
                    color: activeTab === "schemes" ? "#fff" : "rgb(102, 102, 102)",
                    background: activeTab === "schemes" ? theme.dark : 'none rgb(235, 235, 235)'
                  }}
                  onClick={() => {
                    setActiveTab("schemes");
                  }}
                  onMouseOver={(e) => {
                    let target = e.target as HTMLInputElement;
                    target.style.background = theme.light;
                    target.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    let target = e.target as HTMLInputElement;
                    target.style.background = activeTab === "schemes" ? theme.dark : 'none rgb(235, 235, 235)';
                    target.style.color = activeTab === "schemes" ? "#fff" : "rgb(102, 102, 102)";
                  }}
                >
                  <span>Schemes</span>
                </button>
              </li>
            )}
            {propertiesExist && (
              <li className={styles.Tab}>
                <button className={styles.TabLink}
                  style={{
                    color: activeTab === "properties" ? "#fff" : "rgb(102, 102, 102)",
                    background: activeTab === "properties" ? theme.dark : "none rgb(235, 235, 235)"
                  }}
                  id="port-properties-tab"
                  onClick={() => {
                    createPropertiesTab();
                  }}

                  onMouseOver={(e) => {
                    const target = e.target as HTMLInputElement;
                    target.style.background = theme.light;
                    target.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    const target = e.target as HTMLInputElement;
                    target.style.background = activeTab === "properties" ? theme.dark : 'none rgb(235, 235, 235)';
                    target.style.color = activeTab === "properties" ? "#fff" : "rgb(102, 102, 102)";
                  }}
                >
                  <span>Properties</span>
                </button>
              </li>
            )}
          </ul>
        </div>
      </div>
      <div className={styles.TabsContentContainer}>
        {schemesExist && activeTab === "schemes" && (
          <div className={styles.TabsContent}>
            {Object.keys(schemeOrder).map((category, i) => {
              if (category === "HS") return null;
              const image = schemeOrder[category]?.['image'] ? schemeOrder[category]['image'] : `${process.env.NEXT_PUBLIC_APP_CDN_URL}portfolios/_categories/category_${category}.webp`;
              return (
                <Link key={i} href={`${thisLocation}/schemes/${CATEGORIES_MAP[category]}`} passHref prefetch={false} legacyBehavior>
                  <div className={`${styles.PortfolioCard} ${styles.Card}`}
                    onClick={() =>
                      logInternalLinkClick(
                        "Portfolio Schemes Category",
                        `${thisLocation}/schemes/${CATEGORIES_MAP[category]}`, undefined, activePortfolio.id
                      )
                    }
                  >
                    <div className={styles.CardBg} style={{
                      backgroundImage: `url(${image})`,
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }} />
                    <div className={styles.PortfolioCardBody}>
                      <p>{uri === 'Landsec' ? TEXT[`SCHEME_${category}_LABEL_LANDSEC`] : TEXT[`SCHEME_${category}_LABEL_UPPER_PLURAL`]}</p>
                    </div>
                  </div>
                </Link>
              );
            })}

            <div className={`${styles.ViewAll}`}>
              <Link href={`${thisLocation}/schemes`} passHref prefetch={false} legacyBehavior>
                <a>
                  <button className={`${styles.ViewAllButton} ${buttons.primaryButton}`}
                    onClick={() => logInternalLinkClick("Portfolio Schemes All", `${thisLocation}/schemes`, undefined, activePortfolio.id)}
                  >
                    View all Schemes
                  </button>
                </a>
              </Link>
            </div>
          </div>
        )}

        {propertiesExist && activeTab === "properties" && propertiesOrder && (
          <div className={styles.TabsContent}>
            {Object.keys(propertiesOrder).map((category, i) => {
              if (category === "HS") return null;
              const image = propertiesOrder[category]?.['image'] ? propertiesOrder[category]['image'] : `${process.env.NEXT_PUBLIC_APP_CDN_URL}portfolios/_categories/category_${category}.webp`;
              return (
                <Link key={i} href={`${thisLocation}/properties/${CATEGORIES_MAP[category]}`} passHref prefetch={false} legacyBehavior>
                  <div className={`${styles.PortfolioCard} ${styles.Card}`}
                    onClick={() =>
                      logInternalLinkClick(
                        "Portfolio Properties Category",
                        `${thisLocation}/properties/${CATEGORIES_MAP[category]}`, undefined, activePortfolio.id
                      )
                    }
                  >
                    <div className={styles.CardBg} style={{
                      backgroundImage: `url(${image})`,
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }} />
                    <div className={styles.PortfolioCardBody}>
                      <p>{uri === 'Landsec' ? TEXT[`SCHEME_${category}_LABEL_LANDSEC`] : TEXT[`SCHEME_${category}_LABEL_UPPER_PLURAL`]}</p>
                    </div>
                  </div>
                </Link>
              );
            })}

            <div className={`${styles.ViewAll}`}>
              <Link href={`${thisLocation}/shops-to-rent`} passHref prefetch={false} legacyBehavior>
                <a>
                  <button className={`${styles.ViewAllButton} ${buttons.primaryButton}`}
                    onClick={() => logInternalLinkClick("BL Logo Link", "/portfolio/BritishLand", undefined, activePortfolio.id)}
                  >
                    View all Properties
                  </button>
                </a>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioCategoryTabs;
